import { forwardRef } from "react";
import { Button, ButtonProps } from "../../../Button";
import {
  ToolbarItemAppearance,
  WithAriaLabelEnforcement,
} from "../../sharedTypes";
import { ToolbarItemWrapper } from "../ToolbarItemWrapper/ToolbarItemWrapper";
import { useOverFlowItemContext } from "../ToolbarOverflowMenu/ToolbarOverflowMenu";

import cx from "classnames";
import styles from "../../Toolbar.module.scss";
import { Tooltip } from "../../../Tooltip";

/**
 * Base props for the ToolbarButton component
 * @extends Omit<ButtonProps, "appearance" | "aria-label" | "children">
 */
type ToolbarButtonBaseProps = Omit<
  ButtonProps,
  "appearance" | "aria-label" | "children"
> & {
  /**
   * Visual appearance of the button
   * @default ghost
   */
  appearance?: ToolbarItemAppearance;
};

/**
 * Props for the ToolbarButton component
 * @extends WithAriaLabelEnforcement<ToolbarButtonBaseProps>
 */
type ToolbarButtonProps = WithAriaLabelEnforcement<ToolbarButtonBaseProps>;

/**
 * ToolbarButton component for standard clickable actions within a toolbar.
 *
 * Features:
 * - Designed specifically for toolbar integration
 * - Consistent styling with other toolbar items
 * - Automatic tooltip for icon-only buttons
 * - Overflow menu support
 * - Accessibility enforcement for aria-labels
 * - Small size optimized for toolbar layout
 * - Ghost and primary appearance options
 *
 * @example
 * <ToolbarButton icon={BoldIcon} aria-label="Bold text" />
 *
 * @example
 * <ToolbarButton appearance="primary">
 *   Save
 * </ToolbarButton>
 */
const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (props, _ref) => {
    const { appearance = "ghost", className, children, ...rest } = props;

    // Checks if this component is rendered inside the overflow menu and fallbacks when the context is unavailable
    const { isInOverflowMenu = false } = useOverFlowItemContext?.() || {};

    const buttonClassNames = cx(className, styles["toolbar-button-item"]);

    const item = children ? (
      <Button
        appearance={appearance}
        size="small"
        data-anv="toolbar-button"
        className={buttonClassNames}
        {...rest}
      >
        {children}
      </Button>
    ) : (
      <Tooltip>
        <Tooltip.Trigger>
          <Button
            appearance={appearance}
            size="small"
            data-anv="toolbar-button"
            className={buttonClassNames}
            {...rest}
          />
        </Tooltip.Trigger>
        <Tooltip.Content>{rest["aria-label"]}</Tooltip.Content>
      </Tooltip>
    );

    if (isInOverflowMenu) {
      return item;
    }

    return (
      <ToolbarItemWrapper item={{ itemProps: props, itemType: "button" }}>
        {item}
      </ToolbarItemWrapper>
    );
  },
);

ToolbarButton.displayName = "ToolbarButton";

export { type ToolbarButtonProps, ToolbarButton };
